import { useEffect, useContext, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import PayRollLayoutWrapper from "../../components/PayRollLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  Users,
  Search,
  Plus,
  RefreshCcw,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const BASE_URL = "http://nissi-india.com";

// helpers
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

const normalizeStr = (v) => (v ?? "").toString().trim();

// dd/MM/yyyy (ASP) validation like JS
const parseDDMMYYYY = (s) => {
  const v = normalizeStr(s);
  if (!v) return null;
  const [dd, mm, yy] = v.split("/");
  const d = new Date(toInt(yy), toInt(mm) - 1, toInt(dd));
  return Number.isFinite(d.getTime()) ? d : null;
};

const EmployeeJoiningList = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const primaryBtnClass = `bg-${baseColor}-${baseShade}`;
  const primaryHoverClass = `hover:bg-${baseColor}-700`;

  const finYearId = localStorage.getItem("finYearId") || "";
  const branchId = localStorage.getItem("branchId") || "";      // must exist after login
  const branchType = localStorage.getItem("branchType") || "HO";  // "HO" or others

  const screenBadge = useMemo(() => "INFRA SCREEN : C-19", []);

  const inputBase =
    `w-full px-4 py-3 rounded-2xl border outline-none transition ` +
    (isDark
      ? "bg-gray-800/40 border-white/10 text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
      : "bg-white border-gray-200 text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-blue-500");

  const labelBase = `text-sm font-semibold ${isDark ? "text-gray-200" : "text-gray-700"}`;

  const cardBase =
    `rounded-3xl border shadow-xl overflow-hidden ` +
    (isDark
      ? "border-white/10 bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md"
      : "border-gray-200 bg-white");

  const primaryBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl text-white font-bold shadow-lg ` +
    `hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${primaryBtnClass} ${primaryHoverClass}`;

  const ghostBtn =
    `inline-flex items-center justify-center gap-2 px-6 py-3 rounded-2xl font-bold border transition-all duration-200 ` +
    (isDark
      ? "border-white/10 text-white hover:bg-white/10"
      : "border-gray-200 text-gray-800 hover:bg-gray-50");

  // meta dropdowns
  const [toSites, setToSites] = useState([]);
  const [fromSites, setFromSites] = useState([]);

  // filters
  const [showFilters, setShowFilters] = useState(true);
  const [toSite, setToSite] = useState("Select");
  const [fromSite, setFromSite] = useState("Select");
  const [empCode, setEmpCode] = useState("");
  const [empName, setEmpName] = useState("");
  const [fromDate, setFromDate] = useState(""); // dd/MM/yyyy
  const [toDate, setToDate] = useState("");     // dd/MM/yyyy

  // autocomplete
  const [codeSug, setCodeSug] = useState([]);
  const [nameSug, setNameSug] = useState([]);

  // list
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // pagination (client)
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const total = rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const pagedRows = rows.slice((safePage - 1) * pageSize, safePage * pageSize);

  const validateDates = () => {
    const f = parseDDMMYYYY(fromDate);
    const t = parseDDMMYYYY(toDate);
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (f && f > today) {
      toast.warn("Select from date lesser than current date");
      return false;
    }
    if (t && t > today) {
      toast.warn("Select to date lesser than or Equal to Current date");
      return false;
    }
    if (f && t && t < f) {
      toast.warn("Please select from date less than to date");
      return false;
    }
    return true;
  };

  const loadMeta = async () => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }
    if (!String(branchId).trim() || !String(branchType).trim()) {
      toast.error("branchId/branchType missing in localStorage.");
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/employee-joining/meta`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchType),
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Meta load failed");
        return;
      }

      setToSites(res.data.toSites || []);
      setFromSites(res.data.fromSites || []);
      setToSite(String(res.data.defaultToSite || branchId));
      setFromSite("Select");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchList = async (isSearch = false) => {
    if (!String(finYearId).trim()) {
      toast.warn("Please select Financial Year.");
      return;
    }

    if (!validateDates()) return;

    // ASP behavior: if one date filled and other empty -> still allowed? In ASP they validate only comparisons.
    // We'll keep your earlier strict rule OFF here (because ASP allows blank dates).
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/payroll/employee-joining/list`, {
        params: {
          finYearId: Number(finYearId),
          branchId: Number(branchId),
          branchType: String(branchType),

          // filters (keep ASP strings)
          toSite: normalizeStr(toSite) || "Select",
          fromSite: normalizeStr(fromSite) || "Select",
          fromDate: normalizeStr(fromDate),
          toDate: normalizeStr(toDate),
          empCode: normalizeStr(empCode),
          empName: normalizeStr(empName),
        },
      });

      if (!res?.data?.success) {
        toast.error(res?.data?.message || "Failed to load list");
        return;
      }

      setRows(res.data.rows || []);
      setPage(1);

      if ((res.data.rows || []).length === 0) toast.info("No Records Found");
      else if (isSearch) toast.success("Loaded");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  // autocomplete (simple, no extra libs)
  useEffect(() => {
    const t = setTimeout(async () => {
      const q = normalizeStr(empCode);
      if (!q || !normalizeStr(toSite) || toSite === "Select") {
        setCodeSug([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/payroll/employee-joining/autocomplete/code`, {
          params: {
            finYearId: Number(finYearId),
            toSite: String(toSite),
            branchType: String(branchType),
            q,
          },
        });
        if (res?.data?.success) setCodeSug(res.data.items || []);
      } catch {
        // ignore autocomplete errors
      }
    }, 350);
    return () => clearTimeout(t);
  }, [empCode, toSite, finYearId, branchType]);

  useEffect(() => {
    const t = setTimeout(async () => {
      const q = normalizeStr(empName);
      if (!q || !normalizeStr(toSite) || toSite === "Select") {
        setNameSug([]);
        return;
      }
      try {
        const res = await axios.get(`${BASE_URL}/api/payroll/employee-joining/autocomplete/name`, {
          params: {
            finYearId: Number(finYearId),
            toSite: String(toSite),
            branchType: String(branchType),
            q,
          },
        });
        if (res?.data?.success) setNameSug(res.data.items || []);
      } catch {
        // ignore
      }
    }, 350);
    return () => clearTimeout(t);
  }, [empName, toSite, finYearId, branchType]);

  useEffect(() => {
    loadMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // default list (like ASP page load)
    fetchList(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toSite]); // once defaultToSite set, load list

  const handleSearch = async (e) => {
    e?.preventDefault?.();
    await fetchList(true);
  };

  const handleAddNew = () => {
    // ASP: EmployeeJoinEntry.aspx
    navigate("/PayRoll/EmployeeJoiningEntry");
  };

  const showingFrom = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const showingTo = Math.min(safePage * pageSize, total);

  return (
    <PayRollLayoutWrapper>
      <ToastContainer
        position="top-right"
        autoClose={2800}
        hideProgressBar={false}
        closeOnClick
        pauseOnHover
        theme={isDark ? "dark" : "light"}
      />

      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">EMPLOYEE JOINING LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`mx-auto max-w-7xl p-6 mt-6 rounded-3xl shadow-2xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `${currentSkin.glass} bg-white text-black border-gray-200`
          }`}
        >
          {/* Top row */}
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow ${isDark ? "bg-white/10" : "bg-gray-100"}`}>
                <Users className={isDark ? "text-white" : "text-gray-700"} />
              </div>

              <div>
                <div className={`text-2xl font-extrabold ${isDark ? "text-white" : "text-gray-900"}`}>
                  Employee Joining Listing
                </div>
                <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Filter / View / Approve checkbox (approval logic optional)
                </div>
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-blue-900/40 text-blue-100" : "bg-blue-100 text-blue-800"}`}>
                {screenBadge}
              </div>

              <div className={`px-4 py-2 rounded-2xl text-sm font-bold ${isDark ? "bg-emerald-900/40 text-emerald-100" : "bg-emerald-100 text-emerald-800"}`}>
                {total} Records
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button onClick={() => fetchList(false)} className={ghostBtn} disabled={loading}>
                <RefreshCcw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
                Refresh
              </button>

              <button onClick={handleAddNew} className={primaryBtn}>
                <Plus className="w-4 h-4" />
                Add New
              </button>

              <button onClick={() => setShowFilters((v) => !v)} className={ghostBtn}>
                {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <form onSubmit={handleSearch} className={cardBase + " p-6 mb-6"}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <div className="relative">
                  <label className={labelBase}>Employee Code</label>
                  <input
                    value={empCode}
                    onChange={(e) => setEmpCode(e.target.value)}
                    className={inputBase + " mt-2"}
                    placeholder="Type employee code..."
                  />
                  {codeSug.length > 0 && (
                    <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                      {codeSug.slice(0, 10).map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`w-full text-left px-4 py-2 text-sm hover:${isDark ? "bg-white/10" : "bg-gray-50"}`}
                          onClick={() => {
                            setEmpCode(s);
                            setCodeSug([]);
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="relative">
                  <label className={labelBase}>Employee Name</label>
                  <input
                    value={empName}
                    onChange={(e) => setEmpName(e.target.value)}
                    className={inputBase + " mt-2"}
                    placeholder="Type employee name..."
                  />
                  {nameSug.length > 0 && (
                    <div className={`absolute z-20 mt-2 w-full rounded-2xl border shadow-lg overflow-hidden ${isDark ? "bg-gray-900 border-white/10" : "bg-white border-gray-200"}`}>
                      {nameSug.slice(0, 10).map((s, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`w-full text-left px-4 py-2 text-sm hover:${isDark ? "bg-white/10" : "bg-gray-50"}`}
                          onClick={() => {
                            setEmpName(s);
                            setNameSug([]);
                          }}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelBase}>From Site</label>
                  <select value={fromSite} onChange={(e) => setFromSite(e.target.value)} className={inputBase + " mt-2"}>
                    <option value="Select">Select</option>
                    {fromSites.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>To Site</label>
                  <select value={toSite} onChange={(e) => setToSite(e.target.value)} className={inputBase + " mt-2"}>
                    <option value="Select">Select</option>
                    {toSites.map((b) => (
                      <option key={b.id} value={String(b.id)}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelBase}>From Date (dd/MM/yyyy)</label>
                  <input
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    className={inputBase + " mt-2"}
                    placeholder="dd/MM/yyyy"
                  />
                </div>

                <div>
                  <label className={labelBase}>To Date (dd/MM/yyyy)</label>
                  <input
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    className={inputBase + " mt-2"}
                    placeholder="dd/MM/yyyy"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-center">
                <button type="submit" className={primaryBtn} disabled={loading}>
                  <Search className="w-4 h-4" />
                  Search
                </button>
              </div>
            </form>
          )}

          {/* Table */}
          <div className={cardBase}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${primaryBtnClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-bold">S.No</th>
                    <th className="px-4 py-4 text-center font-bold">Joining Date</th>
                    <th className="px-4 py-4 text-left font-bold">Employee Code</th>
                    <th className="px-4 py-4 text-left font-bold">Employee Name</th>
                    <th className="px-4 py-4 text-center font-bold">Department</th>
                    <th className="px-4 py-4 text-center font-bold">From Site</th>
                    <th className="px-4 py-4 text-center font-bold">Approve</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: pageSize }).map((_, idx) => (
                      <tr key={idx} className={isDark ? "bg-gray-900" : "bg-white"}>
                        {Array.from({ length: 7 }).map((__, c) => (
                          <td key={c} className="px-4 py-4">
                            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse" />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className={`px-4 py-12 text-center ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((r, idx) => {
                      const isApproved = String(r.approve).toLowerCase() === "true";
                      const empId = r.empid ?? r.EmpId ?? r.employeeid;

                      return (
                        <tr key={`${empId}-${idx}`} className={`${isDark ? "bg-gray-900" : "bg-white"} hover:${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {(safePage - 1) * pageSize + idx + 1}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.trdate}
                          </td>

                         <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-200" : "text-gray-900"}`}>
  <button
    type="button"
    className="font-bold underline underline-offset-2 hover:opacity-80"
    onClick={() => {
      if (!r.empid) {
        toast.error("empid missing in row");
        return;
      }

      // ✅ EXACT ASP behavior
      sessionStorage.setItem("empid", String(r.empid));
      navigate("/PayRoll/EmployeeJoiningEntry");
    }}
  >
    {r.code}
  </button>
</td>



                          <td className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.empname}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.depnme}
                          </td>

                          <td className={`px-4 py-4 text-sm text-center ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                            {r.brnme}
                          </td>

                          <td className="px-4 py-4 text-center">
                            <input type="checkbox" checked={isApproved} disabled />
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between mt-6 gap-4">
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              Showing <b>{showingFrom}</b> to <b>{showingTo}</b> of <b>{total}</b>
            </div>

            {totalPages > 1 && (
              <div className="inline-flex rounded-2xl overflow-hidden border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className={`px-4 py-2 text-sm font-bold ${
                    safePage === 1
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Prev
                </button>

                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i + 1)}
                    className={`px-4 py-2 text-sm font-bold transition ${
                      safePage === i + 1
                        ? `${primaryBtnClass} text-white`
                        : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className={`px-4 py-2 text-sm font-bold ${
                    safePage === totalPages
                      ? "text-gray-400 cursor-not-allowed"
                      : "hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>

        </div>
      </div>
    </PayRollLayoutWrapper>
  );
};

export default EmployeeJoiningList;
